import CsvIcons from '@/components/FileTypeIcons/CsvIcons'
import DocIcons from '@/components/FileTypeIcons/DocIcons'
import PdfIcons from '@/components/FileTypeIcons/PdfIcons'
import PptxIcons from '@/components/FileTypeIcons/PptxIcons'
import XlsxIcons from '@/components/FileTypeIcons/XlsxIcons'
import React from 'react'
import { IconButton, Text } from '@vnetwork/datacentral-ui/primitives'
import { Paperclip, Trash2, FileImage, FileText, FileVideo } from 'lucide-react'

interface GenericFilePreviewProps {
    name: string
    size: number
    preview: string
    handlePreview: () => void
    thumbnail?: string
    fileType:
        | 'Word'
        | 'Excel'
        | 'PowerPoint'
        | 'CSV'
        | 'Video'
        | 'Text'
        | 'Attachment'
        | 'Image'
        | 'PDF'
    onDelete?: () => void
}

export const getFileIcon = (fileType: string) => {
    // attachment_file_type
    // rebuild fileType to attachment_file_type
    // application/pdf -> PDF
    // application/msword -> Word
    // application/vnd.openxmlformats-officedocument.wordprocessingml.document -> Word
    // application/vnd.ms-excel -> Excel
    // application/vnd.openxmlformats-officedocument.spreadsheetml.sheet -> Excel
    // application/vnd.ms-powerpoint -> PowerPoint
    // application/vnd.openxmlformats-officedocument.presentationml.presentation -> PowerPoint
    // application/vnd.ms-powerpoint -> PowerPoint
    // application/vnd.openxmlformats-officedocument.presentationml.presentation -> PowerPoint
    // application/vnd.ms-powerpoint -> PowerPoint

    switch (fileType?.toLowerCase()) {
        case 'image':
        case 'image/jpeg':
        case 'image/png':
        case 'image/gif':
        case 'image/webp':
        case 'image/svg+xml':
        case 'image/tiff':
        case 'image/bmp':
        case 'image/x-icon':
        case 'image/x-windows-bmp':
        case 'image/x-ms-bmp':
        case 'image/x-ms-png':
        case 'image/x-ms-gif':
        case 'image/x-ms-webp':
        case 'image/x-ms-svg+xml':
        case 'image/x-ms-tiff':
            return <FileImage className="w-5 h-5 text-primary" />
        case 'application/msword':
        case 'word':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        case 'docx':
        case 'doc':
            return <DocIcons className="w-5 h-5 text-primary-text" />
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'excel':
            return <XlsxIcons className="w-5 h-5 text-success-text" />
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        case 'powerpoint':
            return <PptxIcons className="w-5 h-5 text-warning-text" />
        case 'csv':
            return <CsvIcons className="w-5 h-5 text-success-text" />
        case 'video':
            return <FileVideo className="w-5 h-5 text-info-text" />
        case 'text':
            return <FileText className="w-5 h-5 text-muted-foreground" />
        case 'application/pdf':
        case 'pdf':
            return <PdfIcons className="w-5 h-5 text-muted-foreground" />
        default:
            return <Paperclip className="w-5 h-5 text-muted-foreground" />
    }
}

const GenericFilePreview: React.FC<GenericFilePreviewProps> = ({
    name,
    handlePreview,
    fileType,
    thumbnail,
    onDelete,
}) => {
    const icon = getFileIcon(fileType)

    return (
        <div
            onClick={handlePreview}
            className="cursor-pointer overflow-hidden transition-all duration-200 relative"
        >
            {/* File Header */}
            <div className="bg-transparent flex items-center justify-between gap-1">
                <div className="flex items-center gap-1 min-w-0">
                    {icon}
                    <Text as="div" size="sm" weight="medium" truncate>
                        {name}
                    </Text>
                </div>
                {!!onDelete && (
                    <IconButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label="Delete"
                        className="text-danger-text shrink-0"
                        onClick={(event) => {
                            event.stopPropagation()
                            onDelete?.()
                        }}
                    >
                        <Trash2 className="w-4 h-4" />
                    </IconButton>
                )}
            </div>

            {/* File Content */}
            <div className="h-[120px] mt-2 flex items-center justify-center">
                {thumbnail ? (
                    <>
                        <img
                            src={thumbnail}
                            alt={name}
                            className="w-full h-full rounded-sm object-cover"
                            onError={(e) => {
                                // Fallback if image fails to load
                                const target = e.target as HTMLImageElement
                                target.style.display = 'none'
                                const fallback = document.createElement('div')
                                fallback.className =
                                    'w-full h-32 bg-muted rounded-sm flex items-center justify-center'
                                // Static literal, no interpolation: safe innerHTML (#149).
                                fallback.innerHTML =
                                    '<span class="text-muted-foreground text-sm">Preview not available</span>'
                                target.parentNode?.appendChild(fallback)
                            }}
                        />
                    </>
                ) : (
                    icon
                )}
            </div>
        </div>
    )
}

export default GenericFilePreview
